/**
 * ADR-0012 ResponseEnvelope wire codec.
 *
 * quota pre-routing 이 반환하는 429/503 은 envelope 이지만 같은 엔드포인트의 정상 응답은
 * 여전히 flat 이다 (ADR-0012 Amendment 의 혼합 계약). 이 혼합 계약을 읽는 소비자가 각자
 * 판별 규칙을 만들면 같은 published contract 의 validator 가 여러 벌로 갈라져, 한쪽만
 * 느슨해지는 drift 가 생긴다. 판별·검증은 이 모듈 하나에 둔다.
 *
 * 이 모듈은 `APIError` 를 포함한 어떤 상위 계층도 import 하지 않는다. 그래야 오류 표현을
 * 소유한 `errors.ts` 와 envelope 파서인 `response-envelope.ts` 가 순환 없이 함께 의존한다.
 */

/** ADR-0012 지원 major. minor 상향과 unknown field는 forward-compatible로 허용한다. */
export const SUPPORTED_SCHEMA_MAJOR = 1;
const SCHEMA_MAJOR_PATTERN = /^\d+$/;

export interface ResponseEnvelopeErrorDetail {
  code: string;
  retryable: boolean;
  message: string;
  retry_after_seconds?: number;
}

/** status로 갈리는 discriminated union — 반대쪽 키는 서버가 생략한다. */
export type ResponseEnvelope =
  | {
      schema_version: string;
      request_id: string;
      trace_id: string;
      idempotency_key?: string;
      status: "ok";
      data: unknown;
    }
  | {
      schema_version: string;
      request_id: string;
      trace_id: string;
      idempotency_key?: string;
      status: "error";
      error: ResponseEnvelopeErrorDetail;
    };

function schemaMajorOf(schemaVersion: string): number | undefined {
  const majorText = schemaVersion.split(".")[0] ?? "";
  if (!SCHEMA_MAJOR_PATTERN.test(majorText)) {
    return undefined;
  }

  const major = Number(majorText);
  return Number.isSafeInteger(major) ? major : undefined;
}

function isErrorDetail(value: unknown): value is ResponseEnvelopeErrorDetail {
  if (!value || typeof value !== "object") {
    return false;
  }

  const detail = value as Record<string, unknown>;
  if (typeof detail.code !== "string") {
    return false;
  }
  if (typeof detail.retryable !== "boolean") {
    return false;
  }
  if (typeof detail.message !== "string") {
    return false;
  }
  if (detail.retry_after_seconds !== undefined) {
    const seconds = detail.retry_after_seconds;
    if (
      typeof seconds !== "number" ||
      !Number.isSafeInteger(seconds) ||
      seconds < 0
    ) {
      return false;
    }
  }

  return true;
}

/**
 * 서버와 같은 wire invariant를 강제해 producer-consumer drift가 조용히 통과하지
 * 않게 한다. 위반 사유에는 응답 본문 값을 넣지 않아 진단 경로의 정보 노출을 막는다.
 */
export function envelopeViolation(value: unknown): string | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return "body is not a JSON object";
  }

  const wire = value as Record<string, unknown>;
  if (typeof wire.schema_version !== "string") {
    return "schema_version is missing";
  }

  const major = schemaMajorOf(wire.schema_version);
  if (major === undefined) {
    return "schema_version is malformed";
  }
  if (major !== SUPPORTED_SCHEMA_MAJOR) {
    return `unsupported schema major ${major}`;
  }
  if (typeof wire.request_id !== "string") {
    return "request_id is missing";
  }
  if (typeof wire.trace_id !== "string") {
    return "trace_id is missing";
  }

  if (wire.status === "ok") {
    if (!("data" in wire)) {
      return "status=ok without data";
    }
    if ("error" in wire) {
      return "status=ok with error";
    }
    return undefined;
  }

  if (wire.status === "error") {
    if ("data" in wire) {
      return "status=error with data";
    }
    if (!isErrorDetail(wire.error)) {
      return "status=error without a valid error detail";
    }
    return undefined;
  }

  return "unknown status";
}

export type EnvelopeDecoding =
  | { kind: "not-envelope" }
  | { kind: "malformed"; violation: string }
  | {
      kind: "ok";
      requestId: string;
      traceId: string;
      data: unknown;
    }
  | {
      kind: "error";
      requestId: string;
      traceId: string;
      detail: ResponseEnvelopeErrorDetail;
    };

/** envelope 에만 있는 marker key. 값의 타입과 무관하게 키 존재만으로 표방을 본다. */
const ENVELOPE_MARKER_KEYS = ["schema_version", "request_id", "trace_id"];

/**
 * 본문이 envelope 를 표방했는지 판정한다.
 *
 * 값 타입이 아니라 marker key 존재로 판정하는 이유는, backend validator 가
 * `schema_version` 의 누락과 non-string 을 **둘 다** 계약 위반으로 거부하기 때문이다.
 * 값 타입으로 판정하면 `schema_version: 1.1` 같은 producer 결함이 위반이 아니라
 * flat 응답으로 분류돼, 소비자가 다시 error 객체를 문자열화하게 된다.
 *
 * marker 가 없더라도 status=error 에 객체 error 를 실은 본문은 envelope 표방으로 본다 —
 * 정확히 그 형태가 [object Object] 를 만들던 모양이라, flat 로 흘려보내면 이 변경이
 * 막으려던 증상이 그대로 남는다. 반대로 error 가 문자열인 flat 본문은 여기 걸리지 않아
 * 기존 처리가 보존된다.
 */
function claimsEnvelope(value: unknown): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const wire = value as Record<string, unknown>;
  if (ENVELOPE_MARKER_KEYS.some((key) => key in wire)) {
    return true;
  }

  return (
    wire.status === "error" && !!wire.error && typeof wire.error === "object"
  );
}

/**
 * 혼합 계약 응답 본문을 판별한다. 정상 응답이 flat 인 엔드포인트에서도 쓰이므로
 * envelope 가 아닌 본문은 오류가 아니라 `not-envelope` 로 돌려주고, 호출부가 기존 flat
 * 처리를 그대로 이어가게 한다.
 *
 * 판별 결과는 네 갈래를 그대로 유지한다. `malformed` 를 `not-envelope` 로 접으면 계약이
 * 어긋난 순간 소비자가 raw 본문으로 되돌아가 [object Object] 노출이 재현된다.
 */
export function decodeEnvelope(value: unknown): EnvelopeDecoding {
  const violation = envelopeViolation(value);

  if (violation) {
    return claimsEnvelope(value)
      ? { kind: "malformed", violation }
      : { kind: "not-envelope" };
  }

  const envelope = value as ResponseEnvelope;
  if (envelope.status === "error") {
    return {
      kind: "error",
      requestId: envelope.request_id,
      traceId: envelope.trace_id,
      detail: envelope.error,
    };
  }

  return {
    kind: "ok",
    requestId: envelope.request_id,
    traceId: envelope.trace_id,
    data: envelope.data,
  };
}

/**
 * 본문을 텍스트로만 읽는 소비자(FormData 업로드, worker)를 위한 진입점.
 * `decodeEnvelope` 와 같은 네 갈래를 유지한다 — JSON 이 아니면 `not-envelope` 다.
 */
export function decodeEnvelopeText(text: string): EnvelopeDecoding {
  try {
    return decodeEnvelope(JSON.parse(text));
  } catch {
    return { kind: "not-envelope" };
  }
}

/**
 * APIError 체계 밖에서 문자열 메시지만 만드는 소비자(worker, FormData 업로드)를 위한 표시용
 * 요약. envelope 가 아니면 `undefined` 를 돌려줘 호출부가 기존 처리를 이어가게 한다.
 *
 * malformed 도 원문을 그대로 흘리지 않고 위반 사유만 남긴다 — raw 본문을 붙이면 계약이
 * 어긋난 순간 사용자에게 JSON 덩어리가 다시 노출된다.
 */
export function envelopeErrorSummary(
  decoded: EnvelopeDecoding
): string | undefined {
  if (decoded.kind === "error") {
    return `${decoded.detail.message} (${decoded.detail.code})`;
  }
  if (decoded.kind === "malformed") {
    return `server returned a malformed ResponseEnvelope (${decoded.violation})`;
  }
  return undefined;
}
